import { T } from 'gt-react';
import type { TaskTypeResponse } from '@erp/rpc';
import { Card } from 'src/components/ui/card';
import { hasTaskInput } from './taskInputPresence';

function fieldLabel(
  fieldName: string,
  taskType: TaskTypeResponse | null | undefined,
): string {
  return (
    taskType?.inputSchema?.fields.find((field) => field.fieldName === fieldName)
      ?.label ?? fieldName
  );
}

function formatInputValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}

/**
 * Read-only card of the task's validated create-time input. Hidden when the
 * task has no typed payload.
 */
export function TaskInputCard({
  input,
  taskType,
}: {
  input: Record<string, unknown> | null | undefined;
  taskType?: TaskTypeResponse | null;
}) {
  if (!hasTaskInput(input)) return null;

  return (
    <Card className="p-3">
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-stone-500">
        <T context="heading for the task's typed create-time input">Input</T>
      </div>
      <dl className="space-y-1.5">
        {Object.entries(input).map(([fieldName, value]) => (
          <div key={fieldName}>
            <dt className="text-[11px] text-muted-foreground">
              {fieldLabel(fieldName, taskType)}
            </dt>
            <dd className="whitespace-pre-wrap text-sm text-stone-800">
              {formatInputValue(value)}
            </dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}
